import { motion } from "framer-motion";
import { Heart } from "lucide-react";

import { PHOTOS } from "@/lib/data";
const ABOUT_IMG = PHOTOS.about;

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const FUN_FACTS = [
  "Mom of 4 + 3 bonus kids",
  "17 years a Cheer & Dance mom",
  "1 dog · 2 cats · all chaos",
  "90s music takes me back",
  "Foodie · forever traveling",
  "Rock Star Fruit Punch · don't judge",
];

const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#F9F8F6] px-6 md:px-10 lg:px-16 py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1500px] grid grid-cols-12 gap-8 md:gap-16 items-start">
        {/* Image column */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          className="col-span-12 md:col-span-5 lg:col-span-5 lg:col-start-2 md:sticky md:top-28"
        >
          <div className="img-hover relative aspect-[4/5] w-full">
            <img
              src={ABOUT_IMG}
              alt="Jasmine — the photographer behind ManeVzion"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: "center 18%" }}
            />
          </div>
          <p className="overline mt-4">Jasmine · aka Jas · ManeVzion</p>
        </motion.div>

        {/* Text column */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          className="col-span-12 md:col-span-7 lg:col-span-5"
        >
          <p className="overline mb-6">Just Jazzy · About Me</p>
          <h2
            data-testid="about-heading"
            className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2A2A]"
          >
            Hello hello — I'm <span className="italic text-[#C48B71]">Jasmine</span>,
            <br />
            but you can call me Jas.
          </h2>

          <div className="mt-8 space-y-5 text-[#5C5C5C] text-[1.02rem] leading-[1.8] max-w-xl font-light">
            <p>
              I'm the youngest of two amazing parents who laid the foundation for
              everything I am today. Born in Brooklyn, raised in Colorado for
              over 30 years now — and Denver has my whole heart. The views, the
              vibes, the beauty… unmatched. But I'm always ready to see where
              the lens takes me next.
            </p>
            <p>
              After 23 years in the mortgage industry, I stepped away from what
              I knew to fully step into what fills my soul — capturing what I
              call <em className="serif text-[#2A2A2A] not-italic md:italic">Main Visions</em>:
              the priceless, blink-and-you'll-miss-it moments that deserve to be
              remembered forever.
            </p>
            <p>
              I'm a traveling photographer with a passion for turning real-life
              memories into forever moments. Whether it's laughter, love,
              milestones, or just the beauty of everyday life — I want to
              capture it all.
            </p>
            <p className="serif italic text-[#2A2A2A] text-xl leading-snug pt-3">
              "So tell me… what's your <span className="text-[#C48B71]">Main Vision</span>?"
            </p>
          </div>

          {/* Why */}
          <div className="mt-12 border-l-2 border-[#C48B71] pl-6">
            <p className="overline mb-2 inline-flex items-center gap-2">
              <Heart size={12} strokeWidth={1.5} className="text-[#C48B71]" />
              Now let's talk my <em>why</em>
            </p>
            <p className="text-[#5C5C5C] text-[1rem] leading-[1.85] font-light max-w-xl">
              I'm a proud mom of <strong className="text-[#2A2A2A] font-normal">4</strong>, plus
              3 incredible bonus kids — and when I say they are AMAZING, I
              mean it. They're the reason I started photography. They are my
              vision of happiness, my purpose, my inspiration behind every shot
              I take.
            </p>
            <p className="text-[#5C5C5C] text-[1rem] leading-[1.85] font-light mt-4 max-w-xl">
              And if you know anything about me, you know I've been a Cheer &
              Dance Mom for <strong className="text-[#2A2A2A] font-normal">17 years</strong> —
              cheerleading has taken our family across this country, and I plan
              to capture every moment along the way.
            </p>
          </div>

          {/* Fun facts */}
          <div className="mt-12">
            <p className="overline mb-4">A few of my favorites</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {FUN_FACTS.map((f, i) => (
                <li
                  key={f}
                  data-testid={`fun-fact-${i}`}
                  className="serif text-[1.05rem] text-[#2A2A2A] flex items-baseline gap-3"
                >
                  <span className="h-1 w-1 rounded-full bg-[#C48B71] shrink-0 translate-y-[-3px]" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-6 max-w-md border-t border-[#E6E2DA] pt-6">
            <div>
              <p className="overline">Based in</p>
              <p className="serif text-2xl mt-1 text-[#2A2A2A]">Denver Metro</p>
              <p className="text-[#5C5C5C] text-sm font-light mt-1">
                & surrounding cities
              </p>
            </div>
            <div>
              <p className="overline">Travels to</p>
              <p className="serif text-2xl mt-1 text-[#2A2A2A]">Anywhere</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
